import { compact, uniq } from "lodash";

import { type ITimeGranularityV2 } from "@north-beam/nb-common";

import { type TileDescriptor, type TileFilterQuery } from "./types";

const DATE_RANGE_CHOICES = {
  hourly: [] as const,
  daily: [
    "today",
    "yesterday",
    "last3Days",
    "last7Days",
    "last14Days",
    "last30Days",
    "last60Days",
    "last6Months",
    "lastYear",
    "lastMonth",
    "monthToDate",
    "yearToDate",
  ] as const,
  weekly: [
    "thisWeek",
    "lastWeek",
    "last4Weeks",
    "last12Weeks",
    "last26Weeks",
    "last52Weeks",
    "yearToDate",
  ] as const,
  monthly: [
    "yearToDate",
    "last6Months",
    "lastYear",
    "last3Years",
    "last5Years",
  ] as const,
} as const;

const DATE_RANGE_LABELS = {
  today: "Today",
  yesterday: "Yest",
  yesterdayAndToday: "Yest & today",
  todayAndYesterday: "Today & yest",
  last3Days: "3d",
  last7Days: "7d",
  last14Days: "14d",
  last30Days: "30d",
  last60Days: "60d",
  monthToDate: "Mo to date",
  lastMonth: "Last mo",
  thisWeek: "This week",
  lastWeek: "Last week",
  thisWeekAndLastWeek: "This week & last week",
  last4Weeks: "1m",
  last12Weeks: "3m",
  last26Weeks: "6m",
  last52Weeks: "1y",
  yearToDate: "Year to date",
  thisMonth: "This mo",
  thisMonthAndLastMonth: "This mo & last mo",
  last3Months: "3m",
  last6Months: "6m",
  last9Months: "9m",
  last12Months: "12m",
  lastYear: "1yr",
  last3Years: "3yr",
  last5Years: "5yr",
} as const;

export function dateRangeOptionsForTimeGranularity(
  timeGranularity: ITimeGranularityV2,
) {
  return DATE_RANGE_CHOICES[timeGranularity].map((value) => {
    return {
      label: DATE_RANGE_LABELS[value as keyof typeof DATE_RANGE_LABELS],
      value: value,
    };
  });
}

export function isTileOverriden(tile: TileDescriptor) {
  return (
    tile.accountingMode ||
    tile.attributionModel ||
    tile.attributionWindow ||
    tile.compareDateRange ||
    tile.filterQuery ||
    tile.advancedSearch ||
    tile.filters.length > 0
  );
}

export function deriveLevelFromSearch(
  filterQuery: TileFilterQuery | null,
  advancedSearch: any | null,
) {
  let level: string = "campaign";

  if (filterQuery) {
    level = filterQuery.level;
  }

  if (advancedSearch) {
    const results = deriveLevelFromAdvancedSearch(
      { campaignName: "campaign", adsetName: "adset", adName: "ad" },
      advancedSearch,
    );

    if (results.includes("campaign")) {
      level = "campaign";
    }

    if (results.includes("adset")) {
      level = "adset";
    }

    if (results.includes("ad")) {
      level = "ad";
    }
  }

  return level;
}

function deriveLevelFromAdvancedSearch(
  fields: Record<string, string>,
  matcher: any,
) {
  const key = Object.keys(matcher)[0];
  const values = matcher[key];

  if (["and", "or"].includes(key)) {
    const results = values.map((value: any) =>
      deriveLevelFromAdvancedSearch(fields, value),
    );

    return uniq(compact(results.flatMap((e: any) => e)));
  } else {
    let field: { var: string } | null = null;
    let query: string | null = null;

    if (key === "!" && "~" in values) {
      [field, query] = values["~"];
    } else if (key == "!") {
      field = values;
    } else if (key == "!!") {
      field = values;
    } else {
      [field, query] = values;
    }

    return field?.var ? fields[field?.var] ?? "campaign" : "campaign";
  }
}

export function buildAdvancedSearchJson({
  tileFilterQuery,
  tileAdvancedSearch,
  filtersFilterQuery,
  filtersAdvancedSearch,
}: {
  tileFilterQuery: TileFilterQuery | null;
  tileAdvancedSearch: any | null;
  filtersFilterQuery: TileFilterQuery | null;
  filtersAdvancedSearch: any | null;
}) {
  const columnsMap: Record<string, string> = {
    campaign: "campaignName",
    adset: "adsetName",
    ad: "adName",
  };

  if (tileFilterQuery) {
    const columnName = columnsMap[tileFilterQuery.level];

    return {
      contains: [
        {
          var: columnName,
        },
        tileFilterQuery.query.trim(),
      ],
    };
  }

  if (tileAdvancedSearch) {
    return tileAdvancedSearch;
  }

  if (filtersFilterQuery) {
    const columnName = columnsMap[filtersFilterQuery.level];

    return {
      contains: [
        {
          var: columnName,
        },
        filtersFilterQuery.query.trim(),
      ],
    };
  }

  if (filtersAdvancedSearch) {
    return filtersAdvancedSearch;
  }

  return null;
}

const LOGICAL_OPERATORS: Record<string, string> = { and: "and", or: "or" };

const FIELDS: Record<string, string> = {
  campaignName: "Campaign Name",
  adsetName: "Adset Name",
  adName: "Ad Name",
  adKey: "Ad Key",
};

const OPERATORS: Record<
  string,
  (column: string | null, query: string | null) => string | null
> = {
  "==": (column, query) => {
    if (!column) {
      return null;
    }

    return `${column} equals "${query}"`;
  },
  "!=": (column, query) => {
    if (!column) {
      return null;
    }

    return `${column} does not equal "${query}"`;
  },
  startsWith: (column, query) => {
    if (!column) {
      return null;
    }

    return `${column} starts with "${query}"`;
  },
  endsWith: (column, query) => {
    if (!column) {
      return null;
    }

    return `${column} ends with "${query}"`;
  },
  contains: (column, query) => {
    if (!column) {
      return null;
    }

    return `${column} contains "${query}"`;
  },
  "!contains": (column, query) => {
    if (!column) {
      return null;
    }

    return `${column} does not contain "${query}"`;
  },
  "~": (column, query) => {
    if (!column) {
      return null;
    }

    return `${column} matches regex "${query}"`;
  },
  "!~": (column, query) => {
    if (!column) {
      return null;
    }

    return `${column} does not match regex "${query}"`;
  },
  "!": (column) => {
    if (!column) {
      return null;
    }

    return `${column} is empty`;
  },
  "!!": (column) => {
    if (!column) {
      return null;
    }

    return `${column} is not empty`;
  },
};

function jsonLogicToText(fields: Record<string, string>, matcher: any) {
  const key = Object.keys(matcher)[0];
  const values = matcher[key];

  if (["and", "or"].includes(key)) {
    const results = values.map((value: any) => jsonLogicToText(fields, value));

    return compact(results)
      .map((e) => `(${e})`)
      .join(` ${LOGICAL_OPERATORS[key]} `);
  } else {
    let field: { var: string } | null = null;
    let query: string | null = null;
    let operatorFunc: any | null = null;

    if (key === "!" && "~" in values) {
      [field, query] = values["~"];
      operatorFunc = OPERATORS["!~"];
    } else if (key == "!") {
      field = values;
      operatorFunc = OPERATORS[key];
    } else if (key == "!!") {
      field = values;
      operatorFunc = OPERATORS[key];
    } else {
      [field, query] = values;
      operatorFunc = OPERATORS[key];
    }

    const column: string | null = field ? fields[field.var] : null;

    const fragment = operatorFunc ? operatorFunc(column, query) : "";

    return fragment;
  }
}

export function jsonLogicToFriendlyText(matcher: any, breakdowns: string[]) {
  const fields = {
    ...FIELDS,
    ...breakdowns.reduce((acc, e) => {
      acc[`breakdown:${e}`] = e;
      return acc;
    }, {} as Record<string, string>),
  };

  return jsonLogicToText(fields, matcher);
}
